/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InfiniteData,
  InvalidateQueryFilters,
  QueryClient,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQuery,
  UseQueryResult,
  useSuspenseInfiniteQuery,
  UseSuspenseInfiniteQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListAuditLogsRequest,
  ListAuditLogsSecurity,
} from "../models/operations/listauditlogs.js";
import { useGramContext } from "./_context.js";
import {
  InfiniteQueryHookOptions,
  QueryHookOptions,
  SuspenseInfiniteQueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  AuditLogsInfiniteQueryData,
  AuditLogsPageParams,
  AuditLogsQueryData,
  buildAuditLogsInfiniteQuery,
  buildAuditLogsQuery,
  prefetchAuditLogs,
  prefetchAuditLogsInfinite,
  queryKeyAuditLogs,
  queryKeyAuditLogsInfinite,
} from "./auditLogs.core.js";
export {
  type AuditLogsInfiniteQueryData,
  type AuditLogsPageParams,
  type AuditLogsQueryData,
  buildAuditLogsInfiniteQuery,
  buildAuditLogsQuery,
  prefetchAuditLogs,
  prefetchAuditLogsInfinite,
  queryKeyAuditLogs,
  queryKeyAuditLogsInfinite,
};

export type AuditLogsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * list auditlogs
 *
 * @remarks
 * List audit logs across organization and projects.
 */
export function useAuditLogs(
  request?: ListAuditLogsRequest | undefined,
  security?: ListAuditLogsSecurity | undefined,
  options?: QueryHookOptions<AuditLogsQueryData, AuditLogsQueryError>,
): UseQueryResult<AuditLogsQueryData, AuditLogsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildAuditLogsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * list auditlogs
 *
 * @remarks
 * List audit logs across organization and projects.
 */
export function useAuditLogsSuspense(
  request?: ListAuditLogsRequest | undefined,
  security?: ListAuditLogsSecurity | undefined,
  options?: SuspenseQueryHookOptions<AuditLogsQueryData, AuditLogsQueryError>,
): UseSuspenseQueryResult<AuditLogsQueryData, AuditLogsQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildAuditLogsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * list auditlogs
 *
 * @remarks
 * List audit logs across organization and projects.
 */
export function useAuditLogsInfinite(
  request?: ListAuditLogsRequest | undefined,
  security?: ListAuditLogsSecurity | undefined,
  options?: InfiniteQueryHookOptions<
    AuditLogsInfiniteQueryData,
    AuditLogsQueryError
  >,
): UseInfiniteQueryResult<
  InfiniteData<AuditLogsInfiniteQueryData, AuditLogsPageParams>,
  AuditLogsQueryError
> {
  const client = useGramContext();
  return useInfiniteQuery<
    AuditLogsInfiniteQueryData,
    AuditLogsQueryError,
    InfiniteData<AuditLogsInfiniteQueryData, AuditLogsPageParams>,
    QueryKey,
    AuditLogsPageParams
  >({
    ...buildAuditLogsInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

/**
 * list auditlogs
 *
 * @remarks
 * List audit logs across organization and projects.
 */
export function useAuditLogsInfiniteSuspense(
  request?: ListAuditLogsRequest | undefined,
  security?: ListAuditLogsSecurity | undefined,
  options?: SuspenseInfiniteQueryHookOptions<
    AuditLogsInfiniteQueryData,
    AuditLogsQueryError
  >,
): UseSuspenseInfiniteQueryResult<
  InfiniteData<AuditLogsInfiniteQueryData, AuditLogsPageParams>,
  AuditLogsQueryError
> {
  const client = useGramContext();
  return useSuspenseInfiniteQuery<
    AuditLogsInfiniteQueryData,
    AuditLogsQueryError,
    InfiniteData<AuditLogsInfiniteQueryData, AuditLogsPageParams>,
    QueryKey,
    AuditLogsPageParams
  >({
    ...buildAuditLogsInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

export function setAuditLogsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      cursor?: string | undefined;
      projectSlug?: string | undefined;
      actorId?: string | undefined;
      action?: string | undefined;
      subjectType?: string | undefined;
      subjectId?: string | undefined;
      subjectIds?: Array<string> | undefined;
      actingSurface?: string | undefined;
      from?: Date | undefined;
      to?: Date | undefined;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    },
  ],
  data: AuditLogsQueryData,
): AuditLogsQueryData | undefined {
  const key = queryKeyAuditLogs(...queryKeyBase);

  return client.setQueryData<AuditLogsQueryData>(key, data);
}

export function invalidateAuditLogs(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      cursor?: string | undefined;
      projectSlug?: string | undefined;
      actorId?: string | undefined;
      action?: string | undefined;
      subjectType?: string | undefined;
      subjectId?: string | undefined;
      subjectIds?: Array<string> | undefined;
      actingSurface?: string | undefined;
      from?: Date | undefined;
      to?: Date | undefined;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "auditlogs", "list", ...queryKeyBase],
  });
}

export function invalidateAllAuditLogs(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "auditlogs", "list"],
  });
}
